
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ReviewInventoryChips from '@/components/purchase-order/review/ReviewInventoryChips.vue';
import type { PurchaseListItem } from '@/types/purchase-list';
import type { InventoryByVariantId, ReviewInventorySignals } from '@/utils/purchase-order/fetchInventoryByVariantIds';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { isUsableImageUrl } from '@/helpers/variantImage';
import {
  effectiveUnitCost,
  effectiveTax,
  effectiveLineTotal,
  type ReviewLineEdits,
  type ReviewLineEdit,
  type SeededTax,
} from '@/utils/purchase-order/reviewLineEdits';

@Component({ components: { ReviewInventoryChips } })
export default class ReviewItemTable extends Vue {
  /**
   * The usable-image rule, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private isUsableImageUrl = isUsableImageUrl;

  @Prop({ required: true }) public readonly items!: PurchaseListItem[];
  @Prop({ default: 'USD' }) public readonly currencyCode!: string;
  @Prop({ default: 'USD' }) public readonly storeCurrency!: string;
  @Prop({ default: () => ({}) }) public readonly inventoryByVariantId!: InventoryByVariantId;
  @Prop({ default: false }) public readonly inventoryLoading!: boolean;
  @Prop({ default: () => ({}) }) public readonly edits!: ReviewLineEdits;
  @Prop({ default: null }) public readonly seededTax!: SeededTax | null;

  public readonly MAX_QUANTITY = 1000000;

  // Local mirror of item.quantity so the qty field stays controlled across
  // parent refetch timing (preserved from the previous implementation).
  public localQuantities: Record<number, number> = {};

  @Watch('items', { immediate: true, deep: true })
  public onItemsChange(next: PurchaseListItem[]): void {
    if (!Array.isArray(next)) { return; }
    for (const item of next) {
      const fromProp = Number(item.quantity);
      if (!Number.isFinite(fromProp)) { continue; }
      if (this.localQuantities[item.id] !== fromProp) {
        this.$set(this.localQuantities, item.id, fromProp);
      }
    }
  }

  public get isNarrow(): boolean {
    return this.$vuetify.breakpoint.smAndDown;
  }

  public get isForeign(): boolean {
    return this.currencyCode !== this.storeCurrency;
  }

  public get unitCostLabel(): string {
    return this.isForeign ? `Unit cost (${this.currencyCode})` : 'Unit cost';
  }

  public get totalLabel(): string {
    return this.isForeign ? `Total (${this.currencyCode})` : 'Total';
  }

  public subtitle(item: PurchaseListItem): string {
    const parts = [item.variant, item.sku].filter(Boolean);
    return parts.join(' · ') || '—';
  }

  public editFor(item: PurchaseListItem): ReviewLineEdit | undefined {
    return this.edits[item.id];
  }

  public qtyFor(item: PurchaseListItem): number {
    const local = this.localQuantities[item.id];
    if (local != null && Number.isFinite(local)) { return local; }
    return Number(item.quantity) || 0;
  }

  public effectiveCostFor(item: PurchaseListItem): number {
    return effectiveUnitCost(item, this.editFor(item));
  }

  public effectiveSkuFor(item: PurchaseListItem): string {
    return this.editFor(item)?.supplierSku ?? item.supplierSku ?? '';
  }

  public taxPercentFor(item: PurchaseListItem): number {
    const rate = effectiveTax(this.editFor(item), this.seededTax ?? undefined).taxRate ?? 0;
    return Number((rate * 100).toFixed(4));
  }

  public lineTotalFor(item: PurchaseListItem): number {
    return effectiveLineTotal(item, this.editFor(item), this.seededTax ?? undefined, this.currencyCode);
  }

  public formatMoney(value: number): string {
    return CurrencyHelper.formatCurrencyWithCode(Number(value), this.currencyCode);
  }

  public invFor(item: PurchaseListItem): ReviewInventorySignals | undefined {
    return this.inventoryByVariantId[Number(item.variantId)];
  }

  public onQty(item: PurchaseListItem, value: number | string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) { return; }
    const clamped = Math.min(Math.floor(parsed), this.MAX_QUANTITY);
    this.$set(this.localQuantities, item.id, clamped);
    this.$emit('qty-change', { item, quantity: clamped });
  }

  public onSupplierSku(item: PurchaseListItem, value: string): void {
    this.$emit('supplier-sku-change', { item, value });
  }

  public onUnitCost(item: PurchaseListItem, value: number | string): void {
    // An emptied field clears the override (revert to the item cost), not $0.
    const str = String(value).trim();
    const parsed = Number(str);
    const unitCost = str !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
    this.$emit('unit-cost-change', { item, unitCost });
  }

  public onTaxPercent(item: PurchaseListItem, value: number | string): void {
    // An emptied field clears the tax override (Number('') === 0 is finite, so
    // guard the empty string explicitly).
    const str = String(value).trim();
    const parsed = Number(str);
    const percent = str !== '' && Number.isFinite(parsed) ? parsed : undefined;
    this.$emit('tax-percent-change', { item, percent });
  }

  public onRemove(item: PurchaseListItem): void {
    this.$emit('remove', item);
  }
}
